import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import './Account.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';

function AccountSettings() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        mobileNumber: user.mobileNumber || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/account/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage('Profile updated successfully');
        window.location.reload(); // Refresh to get updated user data
      } else {
        setMessage(data.message || 'Failed to update profile');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>Account Settings | {BRAND_NAME}</title>
        <meta name="description" content="Update your account settings and profile information." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account/settings`} />
      </Helmet>
      
      <Layout showFooter={true} showBottomTab={true}>
        <div className="account-page">
        <div className="account-container">
          <div className="account-header">
            <Link to="/account" className="account-back">← Back to Account</Link>
            <h1 className="account-title">Account Settings</h1>
            <p className="account-subtitle">Update your profile information</p>
          </div>

          <div className="account-settings">
            <form onSubmit={handleSubmit} className="account-settings-form">
              <div className="account-form-group">
                <label htmlFor="firstName" className="account-form-label">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="account-form-input"
                  required
                />
              </div>

              <div className="account-form-group">
                <label htmlFor="lastName" className="account-form-label">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="account-form-input"
                  required
                />
              </div>

              <div className="account-form-group">
                <label htmlFor="email" className="account-form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="account-form-input"
                  required
                  disabled
                />
                <p className="account-form-hint">Email cannot be changed</p>
              </div>

              <div className="account-form-group">
                <label htmlFor="mobileNumber" className="account-form-label">Mobile Number <span className="account-form-label__optional">(Optional)</span></label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  className="account-form-input"
                  placeholder="9876543210 or +919876543210"
                  maxLength="15"
                />
                <p className="account-form-hint">Enter 10-digit number (e.g., 9876543210) or full international format</p>
              </div>

              {message && (
                <div className={`account-message ${message.includes('success') ? 'account-message--success' : 'account-message--error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="account-form-submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      </div>
      </Layout>
    </>
  );
}

export default AccountSettings;
